/**
 * Realtime Logger - Centralized logging for realtime events
 * 
 * Provides structured logging for all realtime events in development mode
 * with optional error reporting for production.
 * 
 * Features:
 * - Development mode console logging
 * - Connection state tracking
 * - Subscription/unsubscription tracking
 * - Event logging with timestamps
 * - Error reporting
 * - Performance metrics
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

export interface LogEntry {
  timestamp: string
  level: LogLevel
  category: string
  message: string
  data?: Record<string, unknown>
}

export interface RealtimeMetrics {
  connectionAttempts: number
  successfulConnections: number
  failedConnections: number
  reconnectionAttempts: number
  subscriptions: number
  unsubscriptions: number
  eventsReceived: number
  eventsSent: number
  errors: number
  lastConnectionTime?: Date
  lastDisconnectionTime?: Date
  averageReconnectTime?: number
}

class RealtimeLogger {
  private isDevelopment: boolean
  private logs: LogEntry[] = []
  private maxLogs = 1000 // Keep last 1000 logs in memory
  private metrics: RealtimeMetrics = {
    connectionAttempts: 0,
    successfulConnections: 0,
    failedConnections: 0,
    reconnectionAttempts: 0,
    subscriptions: 0,
    unsubscriptions: 0,
    eventsReceived: 0,
    eventsSent: 0,
    errors: 0,
  }
  private reconnectStartTimes: Map<number, number> = new Map()
  private errorReporter: ((error: Error, context?: Record<string, unknown>) => void) | null = null

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development'
  }

  /**
   * Set custom error reporter for production error tracking
   */
  setErrorReporter(reporter: (error: Error, context?: Record<string, unknown>) => void): void {
    this.errorReporter = reporter
  }

  /**
   * Log a message
   */
  private log(level: LogLevel, category: string, message: string, data?: Record<string, unknown>): void {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data,
    }

    // Add to in-memory logs
    this.logs.push(entry)
    if (this.logs.length > this.maxLogs) {
      this.logs.shift() // Remove oldest log
    }

    // Console logging in development mode
    if (this.isDevelopment) {
      const prefix = `[Realtime:${category}]`
      const logData = data ? [prefix, message, data] : [prefix, message]

      switch (level) {
        case 'debug':
          console.debug(...logData)
          break
        case 'info':
          console.log(...logData)
          break
        case 'warn':
          console.warn(...logData)
          break
        case 'error':
          console.error(...logData)
          break
      }
    }

    // Report errors to external service in production
    if (level === 'error' && this.errorReporter && !this.isDevelopment) {
      const error = new Error(message)
      this.errorReporter(error, { category, ...data })
    }
  }

  /**
   * Connection Events
   */
  logConnectionAttempt(url: string, attempt: number = 1): void {
    this.metrics.connectionAttempts++
    this.log('info', 'Connection', `Attempting connection (attempt ${attempt})`, { url, attempt })
  }

  logConnectionSuccess(url: string, duration?: number): void {
    this.metrics.successfulConnections++
    this.metrics.lastConnectionTime = new Date()
    this.log('info', 'Connection', 'Connection established successfully', { url, duration })
  }

  logConnectionFailure(url: string, error: Error | string, attempt: number = 1): void {
    this.metrics.failedConnections++
    this.metrics.errors++
    const errorMessage = error instanceof Error ? error.message : error
    this.log('error', 'Connection', `Connection failed (attempt ${attempt})`, {
      url,
      error: errorMessage,
      attempt,
    })
  }

  logConnectionClosed(reason?: string, code?: number): void {
    this.metrics.lastDisconnectionTime = new Date()
    this.log('warn', 'Connection', 'Connection closed', { reason, code })
  }

  logConnectionError(error: Error | string): void {
    this.metrics.errors++
    const errorMessage = error instanceof Error ? error.message : error
    this.log('error', 'Connection', 'Connection error', { error: errorMessage })
  }

  /**
   * Reconnection Events
   */
  logReconnectionAttempt(attempt: number, delay: number): void {
    this.metrics.reconnectionAttempts++
    this.reconnectStartTimes.set(attempt, Date.now())
    this.log('info', 'Reconnection', `Reconnection attempt ${attempt} starting`, {
      attempt,
      delay,
    })
  }

  logReconnectionSuccess(attempt: number): void {
    const startTime = this.reconnectStartTimes.get(attempt)
    let duration: number | undefined
    
    if (startTime) {
      duration = Date.now() - startTime
      this.reconnectStartTimes.delete(attempt)
      
      // Update average reconnect time
      if (this.metrics.averageReconnectTime) {
        this.metrics.averageReconnectTime = 
          (this.metrics.averageReconnectTime + duration) / 2
      } else {
        this.metrics.averageReconnectTime = duration
      }
    }

    this.log('info', 'Reconnection', `Reconnection successful after ${attempt} attempts`, {
      attempt,
      duration,
    })
  }

  logReconnectionFailure(attempt: number, maxAttempts: number): void {
    this.reconnectStartTimes.delete(attempt)
    this.log('error', 'Reconnection', `Reconnection failed after ${attempt}/${maxAttempts} attempts`, {
      attempt,
      maxAttempts,
    })
  }

  logReconnectionAborted(reason: string): void {
    this.reconnectStartTimes.clear()
    this.log('warn', 'Reconnection', 'Reconnection aborted', { reason })
  }

  /**
   * Subscription Events
   */
  logSubscription(channel: string, eventType?: string): void {
    this.metrics.subscriptions++
    this.log('info', 'Subscription', `Subscribed to channel`, {
      channel,
      eventType,
      totalSubscriptions: this.metrics.subscriptions,
    })
  }

  logUnsubscription(channel: string, reason?: string): void {
    this.metrics.unsubscriptions++
    this.log('info', 'Subscription', `Unsubscribed from channel`, {
      channel,
      reason,
      totalUnsubscriptions: this.metrics.unsubscriptions,
    })
  }

  logSubscriptionError(channel: string, error: Error | string): void {
    this.metrics.errors++
    const errorMessage = error instanceof Error ? error.message : error
    this.log('error', 'Subscription', `Subscription error`, {
      channel,
      error: errorMessage,
    })
  }

  /**
   * Event Logging
   */
  logEventReceived(channel: string, event: string, payload?: Record<string, unknown>): void {
    this.metrics.eventsReceived++
    
    // Log with payload in development, without in production for privacy
    if (this.isDevelopment) {
      this.log('debug', 'Event', `Event received: ${event}`, {
        channel,
        event,
        payload,
        totalEvents: this.metrics.eventsReceived,
      })
    } else {
      this.log('debug', 'Event', `Event received: ${event}`, {
        channel,
        event,
        totalEvents: this.metrics.eventsReceived,
      })
    }
  }

  logEventSent(channel: string, event: string, payload?: Record<string, unknown>): void {
    this.metrics.eventsSent++
    
    if (this.isDevelopment) {
      this.log('debug', 'Event', `Event sent: ${event}`, {
        channel,
        event,
        payload,
        totalEvents: this.metrics.eventsSent,
      })
    } else {
      this.log('debug', 'Event', `Event sent: ${event}`, {
        channel,
        event,
        totalEvents: this.metrics.eventsSent,
      })
    }
  }

  logEventError(channel: string, event: string, error: Error | string): void {
    this.metrics.errors++
    const errorMessage = error instanceof Error ? error.message : error
    this.log('error', 'Event', `Event processing error`, {
      channel,
      event,
      error: errorMessage,
    })
  }

  /**
   * Token Refresh Events
   */
  logTokenRefreshAttempt(reason: string): void {
    this.log('info', 'Auth', 'Token refresh attempt', { reason })
  }

  logTokenRefreshSuccess(expiresAt?: string): void {
    this.log('info', 'Auth', 'Token refresh successful', { expiresAt })
  }

  logTokenRefreshFailure(error: Error | string): void {
    this.metrics.errors++
    const errorMessage = error instanceof Error ? error.message : error
    this.log('error', 'Auth', 'Token refresh failed', { error: errorMessage })
  }

  logTokenUpdate(channelsUpdated: number): void {
    this.log('info', 'Auth', 'Token updated in active connections', { channelsUpdated })
  }

  /**
   * Notification Events
   */
  logNotificationReceived(type: string, priority: string, notificationId: string): void {
    this.log('debug', 'Notification', `Notification received: ${type}`, {
      type,
      priority,
      notificationId,
    })
  }

  logNotificationBatched(batchKey: string, count: number): void {
    this.log('debug', 'Notification', `Notifications batched`, {
      batchKey,
      count,
    })
  }

  logNotificationBatchFlushed(batchKey: string, count: number): void {
    this.log('debug', 'Notification', `Notification batch flushed`, {
      batchKey,
      count,
    })
  }

  logNotificationError(error: Error | string, context?: Record<string, unknown>): void {
    this.metrics.errors++
    const errorMessage = error instanceof Error ? error.message : error
    this.log('error', 'Notification', 'Notification processing error', {
      error: errorMessage,
      ...context,
    })
  }

  /**
   * Broadcast Events (Backend)
   */
  logBroadcastAttempt(channel: string, event: string, jobId?: string): void {
    this.log('info', 'Broadcast', `Broadcasting event: ${event}`, {
      channel,
      event,
      jobId,
    })
  }

  logBroadcastSuccess(channel: string, event: string, jobId?: string): void {
    this.log('info', 'Broadcast', `Broadcast successful: ${event}`, {
      channel,
      event,
      jobId,
    })
  }

  logBroadcastFailure(channel: string, event: string, error: Error | string, jobId?: string): void {
    this.metrics.errors++
    const errorMessage = error instanceof Error ? error.message : error
    this.log('error', 'Broadcast', `Broadcast failed: ${event}`, {
      channel,
      event,
      error: errorMessage,
      jobId,
    })
  }

  /**
   * Performance Metrics
   */
  logPerformanceMetric(metric: string, value: number, unit: string = 'ms'): void {
    this.log('debug', 'Performance', `${metric}: ${value}${unit}`, {
      metric,
      value,
      unit,
    })
  }

  /**
   * Get current metrics
   */
  getMetrics(): RealtimeMetrics {
    return { ...this.metrics }
  }

  /**
   * Reset metrics
   */
  resetMetrics(): void {
    this.metrics = {
      connectionAttempts: 0,
      successfulConnections: 0,
      failedConnections: 0,
      reconnectionAttempts: 0,
      subscriptions: 0,
      unsubscriptions: 0,
      eventsReceived: 0,
      eventsSent: 0,
      errors: 0,
    }
    this.reconnectStartTimes.clear()
    this.log('info', 'Metrics', 'Metrics reset')
  }

  /**
   * Get recent logs
   */
  getLogs(count?: number, level?: LogLevel): LogEntry[] {
    let logs = this.logs

    if (level) {
      logs = logs.filter(log => log.level === level)
    }

    if (count) {
      return logs.slice(-count)
    }

    return [...logs]
  }

  /**
   * Clear logs
   */
  clearLogs(): void {
    this.logs = []
    this.log('info', 'Logger', 'Logs cleared')
  }

  /**
   * Export logs as JSON
   */
  exportLogs(): string {
    return JSON.stringify({
      logs: this.logs,
      metrics: this.metrics,
      exportedAt: new Date().toISOString(),
    }, null, 2)
  }

  /**
   * Get summary statistics
   */
  getSummary(): Record<string, unknown> {
    const uptime = this.metrics.lastConnectionTime
      ? Date.now() - this.metrics.lastConnectionTime.getTime()
      : 0

    const successRate = this.metrics.connectionAttempts > 0
      ? (this.metrics.successfulConnections / this.metrics.connectionAttempts) * 100
      : 0

    return {
      uptime,
      successRate: successRate.toFixed(2) + '%',
      totalEvents: this.metrics.eventsReceived + this.metrics.eventsSent,
      activeSubscriptions: this.metrics.subscriptions - this.metrics.unsubscriptions,
      errorRate: this.metrics.errors,
      averageReconnectTime: this.metrics.averageReconnectTime
        ? `${Math.round(this.metrics.averageReconnectTime)}ms`
        : 'N/A',
    }
  }
}

// Export singleton instance
const realtimeLogger = new RealtimeLogger()

export default realtimeLogger
